<template>
  <div>
    <b-row>
      <b-col md="12" lg="6">
        <order-details 
          :order="order" 
          :gpNote="gpNote" 
          :sickNote="sickNote" 
          :refferalLetter="refferalLetter" 
          :prescription="prescription"
          @change-appointment="handleAppointmentChange"
        />

        <b-card v-if="order && order.isRepeatPrescription">
          <div slot="header">
            <i class='fa fa-align-justify'></i><strong class='header-spacing'> Repeat Prescription info</strong>
          </div>

          <b-form-group id="repeatFrequencyGroup" label="Repeat frequency" label-for="repeatFrequency" label-cols="6">
            <b-form-input name="repeatFrequency" type="text" :value="repeatFrequencyLabel" disabled />
          </b-form-group>
          <b-form-group id="repeatNoOfMonthsGroup" label="Number of months" label-for="repeatNoOfMonths" label-cols="6">
            <b-form-input name="repeatNoOfMonths" type="text" :value="repeatNoOfMonths" disabled />
          </b-form-group>
          <b-form-group id="repeatValidUntilGroup" label="Next consultation date" label-for="repeatValidUntil" label-cols="6">
            <b-form-input name="repeatValidUntil" type="text" :value="repeatValidUntil" disabled />
          </b-form-group>
          <div v-if="repeatCycleCancelable" class="mt-2">
            <b-button variant="outline-danger" size="sm" @click="showRepeatCancelModal = true">
              Cancel repeat prescription
            </b-button>
          </div>
          <p v-else-if="repeatCycleProcessStatus === 'CANCELED'" class="text-muted small mb-0 mt-2">
            Repeat prescription cycle is cancelled.
          </p>
        </b-card>

        <b-card v-if="order && consLogTimes && consLogTimes.patientLoggedTime && consLogTimes.doctorLoggedTime">
          <div slot="header">
            <i class='fa fa-align-justify'></i><strong class='header-spacing'> Call details</strong>
          </div>
          
          <div class="call-details-box">
            <p>
              <span class="key">APPOINTMENT SLOT</span>
              <span class="val fw-600">{{formatDateTime(order.appointment.datetime)}}</span>
            </p>
            <p>
              <span class="key">PATIENT CALL STARTED AT</span>
              <span class="val">{{formatDateTime(consLogTimes.patientLoggedTime)}}</span>
            </p>
            <p>
              <span class="key">CLINICIAN CALL STARTED AT</span>
              <span class="val">{{formatDateTime(consLogTimes.doctorLoggedTime)}}</span>
            </p>
          </div>
        </b-card>

        <b-card>
          <div slot="header">
            <i class='fa fa-align-justify'></i><strong class='header-spacing'> Delivery address</strong>
          </div>
          
          <div class="delivery-address-section">
            <div class="delivery-address-block">
              <h6 class="delivery-label">Current delivery address</h6>
              <div class="delivery-address-box">
                <p class="delivery-address-text">{{ formattedDeliveryAddress }}</p>
              </div>
              <b-button class="mt-3" @click="openDeliveryAddressModal">
                Change delivery address
              </b-button>
            </div>

            <div v-if="deliveryDetails && (deliveryDetails.status === 'ORDER_DISPENSED' || deliveryDetails.status === 'DELIVERED')">
              <hr/>
              <div class="delivery-info-block">
                <div class="delivery-info-content">
                  <div class="delivery-info-header">
                    <span class="delivery-info-label">Delivery information</span>
                    <b-badge variant="warning" class="order-status-badge">{{ getOrderStatus() }}</b-badge>
                  </div>
                
                  <p class="delivery-carrier">
                    <i class="fa fa-circle"></i>
                    <span class="carrier-name">{{deliveryDetails.partnerName}}</span>
                    <span class="tracking-id">{{deliveryDetails.trackingId}}</span>
                  </p>
                </div>
                <b-button variant="outline-success" size="sm" class="copy-id-btn" @click="copyTrackingId">
                  <i class="fa fa-copy"></i> Copy ID
                </b-button>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col>
        <patient-details :patient="order.patient" :order="order"/>
        <b-card v-if="docTypes.length > 0 || timeline.length > 0 || hasAdditionalPayments" class="additional-services">
          <div slot="header">
            <i class='fa fa-align-justify'></i><strong class='header-spacing'> Additional services</strong>
            <b-badge v-if="hasAdditionalPayments" variant="success" class="ml-2">NEW</b-badge>
          </div>

          <div v-if="hasAdditionalPayments" class="payment-status-panel">
            <p class="title">Additional payment status</p>
            <p>
              <span class="key">Delivery type:</span>
              <span class="val">{{ order.deliveryType || 'N/A' }}</span>
            </p>
            <p>
              <span class="key">Order status:</span>
              <span class="val">{{ order.status | orderStatus }}</span>
            </p>
            <p>
              <span class="key">Document:</span>
              <span class="val">{{ paymentStatusLabel(additionalPayments.documentPaymentStatus) }}</span>
            </p>
            <p>
              <span class="key">Medication:</span>
              <span class="val">{{ paymentStatusLabel(additionalPayments.medicationPaymentStatus) }}</span>
            </p>
            <p>
              <span class="key">Delivery:</span>
              <span class="val">{{ paymentStatusLabel(additionalPayments.deliveryPaymentStatus) }}</span>
            </p>
          </div>

          <div v-if="isAdditionalPaymentMismatch" class="action-row mt-3">
            <p class="title">Payment captured, status not updated</p>
            <p>
              Additional payments are captured, but this order is still
              <strong>{{ order.status | orderStatus }}</strong>.
              Completing it will set
              <strong>{{ expectedNextStatusLabel }}</strong>
              and send {{ expectedEmailLabel }}.
            </p>
          </div>

          <b-button
            v-if="hasAdditionalPayments"
            class="mt-3"
            variant="success"
            :disabled="!canCompleteAdditionalPayment || completingAdditionalPayment"
            @click="completePaymentModal = true"
          >
            Complete additional payment
            <b-badge variant="light" class="ml-2 new-feature-badge">NEW</b-badge>
          </b-button>

          <!-- <div class="action-row">
            <p class="title">Additional services document consent status</p>
            <p>
              <span class="key">Consent status:</span>
              <span v-if="consentStatus == 'Pending'" class="val pending">{{consentStatus}}...</span>
              <span v-if="consentStatus != 'Pending'" class="val" 
                :class="{ approved: consentStatus == 'Approved', declined: consentStatus == 'Declined' }"
              >{{consentStatus}}</span>
            </p>
          </div>

          <hr> -->
          
          <div v-if="docTypes.length > 0" class="share-docs-row">
            <p class="title">Share documents</p>
            <p class="subtitle">What are the documents you want to share with patient?</p>

            <select v-model="consentResendDocType" class="doc-type-select">
              <option v-for="c in docTypes" :value="c.key" :key="c.key">
                {{c.value}}
              </option>
            </select>
          </div>
          <b-button v-if="docTypes.length > 0" class="mt-3" @click="resendPaymentLink()" :disabled="isDocumentPaymentMade">
            Send payment link
          </b-button>

          <hr v-if="docTypes.length > 0 && timeline.length > 0">
          
          <div class="history-row" v-if="timeline.length > 0">
            <p class="title">Additional services history</p>

            <div 
              class="history-item" 
              v-for="item in timeline" 
              :key="item.key"
              :class="{
                'pending': item.tone === 'pending' || item.name.toLowerCase().includes('proposed'),
                'failed': item.tone === 'failed'
              }"
            >
              <p>
                <span class="timestamp">
                  {{ item.timestamp }}
                </span>
                <span class="name">
                  {{ item.name }}
                </span>
              </p>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <br/>

    <b-card v-if="order && order.workflow">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'>GP Details</strong>
      </div>

      <template v-if="!!patient && !patient.gp">
        <h5>Setup GP to patient</h5>
        <b-form-group
          label="Search GP"
          label-for="gpSearchInput"
          description="Search GP by ODS code"
        >
          <b-form-input
            id="gpSearchInput"
            v-model="gpSearch"
            debounce="500"
            type="text"
            required
          ></b-form-input>
        </b-form-group>

        <div class="text-center" v-if="gpList === null">
          Please wait
        </div>

        <div class="d-flex justify-content-center" v-if="gpList !== null && gpList.length === 0">
          <div>
            <h6>Don't see options to choose?</h6>
            <ul>
              <li>Check if the ODS number is correct</li>
              <li>
                <b-link v-b-modal.addGPModal >Add a GP manually</b-link>
              </li>
            </ul>
          </div>
        </div>

        <div class="text-center" v-if="gpList !== null && gpList.length > 0">
          <b-row :cols="2" class="gp-list-container">
            <b-col v-for="(item, index) in gpList" :key="index">
              <div class="gp-item" :class="{selected : isSelectedGP(item)}" @click="selectedGP = item">
                <p>
                  <b>{{item.organizationName}} {{item.odsCode ? ` (${item.odsCode})` : ''}}</b> <br/>
                  {{getGPContactNumber(item.phone)}} <br/>
                  <i>{{getGPAddress(item.address)}}</i>
                </p>
              </div>
            </b-col>
          </b-row>
          <b-button class="primary pull-right mt-3" @click="setupGPtoPatient" v-if="!!selectedGP">Setup GP to patient</b-button>
        </div>
      </template>

      <template v-if="!!patient && patient.gp">
        <h4>{{patient.gp.organizationName}}</h4>
        <b-row>
          <b-col>
            <h5><u>Details</u></h5>
            <table>
              <tr>
                <th>ODS number</th>
                <td>: {{patient.gp.odsCode}}</td>
              </tr>
              <tr>
                <th>Organization ID</th>
                <td>: {{patient.gp.organizationID}}</td>
              </tr>
              <tr>
                <th>Organization Type</th>
                <td>: {{patient.gp.organisationType}}</td>
              </tr>
              <tr>
                <th>Organization Type ID</th>
                <td>: {{patient.gp.organisationTypeID}}</td>
              </tr>
            </table>
          </b-col>
          <b-col>
            <h5><u>Contact</u></h5>
            <table>
              <tr>
                <th>Address</th>
                <td>: {{getGPAddress(patient.gp.address)}}</td>
              </tr>
              <tr>
                <th>Phone</th>
                <td>: {{getGPContactNumber(patient.gp.phone)}}</td>
              </tr>
              <tr>
                <th>Fax</th>
                <td>
                  : 
                  <template v-if="!!patient.gp.fax">
                    {{patient.gp.fax}}
                  </template>
                  <template v-if="!patient.gp.fax">
                    <b-button size="sm" @click="openUpdateGPModal('fax')">Add Fax</b-button>
                  </template>
                </td>
              </tr>
              <tr>
                <th>Email</th>
                <td>
                  : 
                  <template v-if="!!patient.gp.email">
                    {{patient.gp.email}}
                  </template>
                  <template v-if="!patient.gp.email">
                    <b-button size="sm" @click="openUpdateGPModal('email')">Add Email</b-button>
                  </template>
                </td>
              </tr>
              <tr>
                <th>Website</th>
                <td>: {{patient.gp.url}}</td>
              </tr>
            </table>
          </b-col>
        </b-row>

      </template>

      <template v-if="!!patient && patient.gp">
        <template v-if="order.workflow.GP_NOTE_ACTION_STATUS && order.workflow.GP_NOTE_ACTION_STATUS === 'SUCCESSFUL'">
          <h5><u>Successfully shared GP notes</u></h5>
          <table>
            <tr>
              <th>Shared on</th>
              <td>: {{order.workflow.GP_NOTE_ACTION_DATETIME}}</td>
            </tr>
            <tr>
              <th>Shared method</th>
              <td>: {{order.workflow.GP_NOTE_ACTION}}</td>
            </tr>
          </table>
        </template>
        <template v-else>
          <!-- TODO  v-if="order.status !== 'BOOKED' && (patient.gp.email || patient.gp.fax) && patient.preferences.shareDetailsWithGp === 'true'" -->
          <b-button 
            class="primary pull-right mt-3" 
            @click="onClickShareNotesWithGP" 
            v-if="order.status !== 'BOOKED' && (patient.gp.email) && patient.preferences.shareDetailsWithGp === 'true'"
          >
            Share notes with GP
          </b-button>
        </template>
      </template>

    </b-card>

    <voucher-info v-if="order.coupon" :order="order"/>

    <additional-order-info :order="order"/>

    <prescription-details :order="order" :prescription="prescription"/>

    <b-card v-if="order?.transcriptionTransferRecordDTOS && order.transcriptionTransferRecordDTOS.length > 0 && completeRecord?.doctorFirstName">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Authoriser Basic Info</strong>
      </div>
      <p><b>First Name:</b> <span>{{completeRecord.doctorFirstName}}</span></p>
      <p><b>Last Name:</b> <span>{{completeRecord.doctorLastName}}</span></p>
      <p><b>Email:</b> <span>{{completeRecord.doctorEmail}}</span></p>
      <p><b>Reg No:</b> <span>{{completeRecord.id}}</span></p>
      <p><b>Consultant Type:</b> <span>{{completeRecord.consultationType}}</span></p>
    </b-card>

    <b-card v-if="order && order.workflow && gpNote?.details">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Clinician Notes</strong>
      </div>

      <p><b>Relevant past medical history:</b> <span>{{ gpNote.details.relevantPastMedicalHistory ?? 'N/A' }}</span></p>
      <p><b>Relevant past family history:</b> <span>{{ gpNote.details.relevantPastFamilyHistory ?? 'N/A' }}</span></p>
      <p><b>Drug history (past and present):</b> <span>{{ gpNote.details.medicationHistory ?? 'N/A' }}</span></p>
      <p><b>Allergy information:</b> <span>{{ gpNote.details.allergyInformation ?? 'N/A' }}</span></p>
      <p><b>Examination findings:</b> <span>{{ gpNote.details.examinationFindings ?? 'N/A' }}</span></p>
      <p><b>Patient is at the registered location:</b> <span>{{ gpNote.details.patientAtRegLocation == 'true' ? 'Yes' : 'No' }}</span></p>
      <p><b>Does the patient require a follow up consultation:</b> <span>{{ gpNote.details.followUpConsultation == 'true' ? 'Yes' : 'No' }}</span></p>
      <p><b>Share summary with GP:</b> <span>{{ gpNote.details.shareDetailsWithGp == 'true' ? 'Yes' : 'No' }}</span></p>
    </b-card>

    <b-card v-if="order && order.workflow && gpNote?.details">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Patient Notes</strong>
      </div>

      <p><b>Presenting complaint:</b> <span>{{ gpNote.details.presentingComplaint ?? 'N/A' }}</span></p>
      <p><b>Is the Patient feeling any pain:</b> <span>{{ gpNote.details.pain == 'true' ? 'Yes' : 'No' }}</span></p>
      <p><b>Differential diagnosis:</b> <span>{{ gpNote.details.differentialDiagnosis ?? 'N/A' }}</span></p>
      <p><b>Differential diagnosis notes:</b> <span>{{ gpNote.details.differentialDiagnosisNotes ?? 'N/A' }}</span></p>
      <p><b>Recommended actions:</b> <span>{{ gpNote.details.recommendedActions ?? 'N/A' }}</span></p>
      <p><b>Notes for patient record:</b> <span>{{ gpNote.details.additionalNotes ?? 'N/A' }}</span></p>
      <p><b>Referral details:</b> <span>{{ gpNote.details.patientReferredDetails ?? 'N/A' }}</span></p>
    </b-card>

    <b-card v-if="order && order.workflow && order.transcriptionTransferRecordDTOS">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Order Delivery Notes</strong>
      </div>
      <b-form-group id="deliveryNotesLabel" label="Delivery Notes" label-for="deliveryNotes" label-cols="5">
        <b-form-textarea class="mb-1" name="deliveryNotes" v-model="order.workflow.PHARMACY_DELIVER_NOTES" :rows="3" :max-rows="3"/>
      </b-form-group>
      <b-button class="primary pull-right" @click="addDeliveryNotes()">Add delivery notes</b-button>
    </b-card>

    <b-card v-if="this.history && this.history.length > 0">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Order History</strong>
      </div>

      <OrderHistory :items="history" locale="en-US" timeZone="Europe/London" />
    </b-card>

    <b-card v-if="prescription && prescription.id">
      <div slot="header">
        <i class='fa fa-align-justify'></i><strong class='header-spacing'> Delivery details</strong>
      </div>

      <b-row>
        <b-col md="12" xl="4">
          <br/>
          <b-form-group label="How would the patient like to receive his medication?" label-for="basicRadiosCustom" label-class="question-title">
            <b-form-radio-group v-if="order.formType" v-model="deliveryOnly" stacked disabled>
              <b-form-radio value="true" @click.native="findPharmacies()">Collected from local pharmacy</b-form-radio>
              <b-form-radio value="false" @click.native="findPharmacies()">Delivered from local pharmacy</b-form-radio>
            </b-form-radio-group>
            <b-form-radio-group v-if="!order.formType" v-model="deliveryOnly" stacked>
              <b-form-radio value="false" @click.native="findPharmacies()">Collected from local pharmacy</b-form-radio>
              <b-form-radio value="true" @click.native="findPharmacies()">Delivered from local pharmacy</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <br/>

          <div v-if="!order.formType" class="form_group form_group--nested">
            <b-form-group label="Enter your postcode to select your pharmacy:" label-for="postcode" label-class="question-title">
              <b-form-group id="pharmacyIdDescription" description="This field has been pre-populated with the order's pharmacy postcode">
                <b-row class="form-group">
                  <b-col cols="9">
                    <b-input-group class="no-padding">
                      <b-form-input type="search" placeholder="Enter your postcode" v-model="postcode" name="postcode" @search="findPharmacies()" style="min-width:35%"/>
                      <b-input-group-append>
                        <b-button variant="primary" class="btn btn--2" v-on:click="findPharmacies()" style="min-width:30%">Find pharmacies</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-form-group>
          </div>

          <div v-if="order.formType" class="form_group form_group--nested">
            <b-form-group label="Selected postcode for your pharmacy" label-for="postcode" label-class="question-title">
              <b-form-group id="pharmacyIdDescription" description="This field has been pre-populated with the order's pharmacy postcode">
                <b-row class="form-group">
                  <b-col cols="9">
                    <b-input-group class="no-padding">
                      <b-form-input type="text" v-model="postcode" name="postcode" @search="findPharmacies()" style="min-width:35%" disabled/>
                    </b-input-group>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-form-group>
          </div>

          <br/>

          <div class="form_group form_group--nested">
            <b-form-group v-if="!order.formType" label="Enter a Pharmacy ID or pick a pharmacy using the map:" label-for="pharmacyId" label-class="question-title">
              <b-form-group id="pharmacyIdDescription" description="This field has been pre-populated with the order's pharmacy ID">
                <b-row class="form-group">
                  <b-col cols="9">
                    <b-input-group class="no-padding" style="min-width:100%">
                      <b-form-input type="search" placeholder="Enter a Pharmacy ID" v-model="pharmacyId" name="pharmacyId" @search="findPharmacies()"/>
                    </b-input-group>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-form-group>

            <b-form-group v-if="order.formType" label="Selected Pharmacy ID" label-for="pharmacyId" label-class="question-title">
              <b-form-group id="pharmacyIdDescription" description="This field has been pre-populated with the order's pharmacy ID">
                <b-row class="form-group">
                  <b-col cols="9">
                    <b-input-group class="no-padding" style="min-width:100%">
                      <b-form-input type="text" v-model="pharmacyId" name="pharmacyId" @search="findPharmacies()" disabled/>
                    </b-input-group>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-form-group>
          </div>
        </b-col>

        <b-col v-if="!order.formType">
          <p v-if="findPharmacyButtonClicked && !showMap">
            <br /><br /><br />
            Searching...
          </p>
          <div v-if="showMap" class="map_canvas">
              <gmap-map :center="postCodeLocation" ref="map" :zoom="9" style="width: 100%; height: 450px">
                <gmap-marker v-for="p in pharmacies" :key="p.id" @click="selectPharmacy(p)" :position="{lat: p.latitude, lng: p.longitude}" :icon="'https://cdn2.iconfinder.com/data/icons/color-svg-vector-icons-2/512/map_marker_base-48.png'" :opacity="p.member && p.active ? 1 : 0.3">
                  <gmap-info-window :opened="pharmacyId === p.id">
                    <label class="mb-0 text-primary"><h5>{{p.name}}</h5></label> <br/>
                    <span v-if="p.active && p.memberType === 'GOLD'" class="text-primary"><label><strong>Gold Member Pharmacy</strong></label><br/></span>
                    <span v-if="p.active && p.memberType === 'SILVER'" class="text-primary"><label><strong>Silver Member Pharmacy</strong></label><br/></span>
                    <span v-if="p.memberType === 'NONE'" class="text-danger"><label><strong>Non-Member Pharmacy</strong></label><br/></span>
                    <label><strong>Pharmacy ID:</strong> {{p.id}}</label><br/>
                    <label><strong>Email:</strong> {{p.email}}</label><br/>
                    <label><strong>Phone:</strong> {{p.phone}}</label><br/>
                    <b-row v-if="p.address">
                      <b-col cols="4" class="pr-0"><strong>Address:</strong></b-col>
                      <b-col class="pl-0" style="margin-left: -6%">
                        <span><label>{{p.address.line1}} {{p.address.line2}}</label><br/></span>
                        <span v-if="p.address.county"><label>{{p.address.county}}</label><br/></span>
                        <span><label>{{p.address.city}} - {{p.address.postcode}}</label></span>
                      </b-col>
                    </b-row>
                    <span v-if="p.preferences && p.preferences.openingTimes">
                      <label><strong>Opening times:</strong> {{p.preferences.openingTimes}} <br/></label>
                    </span>
                    <span v-if="deliveryOnly">
                      {{p.deliveryNotes}} <br/>
                      <label v-if="p.preferences && p.preferences.deliveryCharge"><strong>Delivery charge:</strong> {{p.preferences.deliveryCharge}}<br/></label>
                    </span>
                    <span v-if="p.settings && p.settings.DOES_VACCINES">
                      <br/>
                      <label><strong>Does vaccines: </strong>Yes</label>
                    </span>
                  </gmap-info-window>
                </gmap-marker>
              </gmap-map>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-3" v-if="!order.formType">
          <b-col xs="6" md="4" class="mb-1">
            <b-button type="button" variant="outline-danger" class="btn-block" @click="reset()">Reset</b-button>
          </b-col>
          <b-col xs="6" md="4">
            <b-button type="button" variant="primary" class="btn-block" @click="confirmModal = true">Confirm</b-button>
          </b-col>
        </b-row>
    </b-card>

    <b-modal title="Confirm delivery details" centered class="modal-success" v-model="confirmModal" @ok="confirm()" ok-title="Yes" ok-variant="success" cancel-title="No" cancel-variant="outline-danger">
        <strong class="text-muted">PLEASE NOTE:</strong>
        <span class="text-danger">This action is not reversible, are you sure you want to confirm this order's delivery details?</span>
    </b-modal>

    <b-modal
      centered
      v-model="completePaymentModal"
      ok-title="Complete and send email"
      ok-variant="success"
      cancel-title="Cancel"
      cancel-variant="outline-danger"
      :ok-disabled="completingAdditionalPayment"
      @ok="completeAdditionalPayment"
    >
      <template slot="modal-title">
        Complete additional payment
        <b-badge variant="success" class="ml-2">NEW</b-badge>
      </template>
      <p>This will move the order and send the matching email.</p>
      <p><span class="key">Delivery type:</span> {{ order.deliveryType || 'N/A' }}</p>
      <p><span class="key">Document:</span> {{ paymentStatusLabel(additionalPayments.documentPaymentStatus) }}</p>
      <p><span class="key">Medication:</span> {{ paymentStatusLabel(additionalPayments.medicationPaymentStatus) }}</p>
      <p><span class="key">Delivery:</span> {{ paymentStatusLabel(additionalPayments.deliveryPaymentStatus) }}</p>
      <p>
        <span class="key">Status:</span>
        {{ order.status | orderStatus }}
        &rarr;
        {{ expectedNextStatusLabel }}
      </p>
      <p><span class="key">Email:</span> {{ expectedEmailLabel }}</p>
    </b-modal>

    <b-modal 
      id="addGPModal" 
      title="Add GP" 
      ok-title='Add GP'
      @ok="addGP"
      @cancel="clearGP" 
      @close="clearGP"
    >
      <b-container fluid>
        <div style="padding: 10px;">
          <b-form-group
              id="odsCodeGroup"
              label="ODS Code"
              label-for="odsCode"
              label-cols="9"
          >
              <b-form-input
                  name="odsCode"
                  type="text"
                  v-model="gp.odsCode"
              />
          </b-form-group>
          <b-form-group
              id="organizationIDGroup"
              label="Organization ID"
              label-for="organizationID"
              label-cols="9"
          >
              <b-form-input
                  name="organizationID"
                  type="text"
                  v-model="gp.organizationID"
              />
          </b-form-group>
          <b-form-group
              id="organizationNameGroup"
              label="Organization Name"
              label-for="organizationName"
              label-cols="5"
          >
              <b-form-input
                  name="organizationName"
                  type="text"
                  v-model="gp.organizationName"
              />
          </b-form-group>
          <b-form-group
              id="organizationTypeGroup"
              label="Organization Type"
              label-for="organizationType"
              label-cols="5"
          >
              <b-form-input
                  name="organizationType"
                  type="text"
                  v-model="gp.organisationType"
              />
          </b-form-group>
          <b-form-group
              id="organizationTypeIDGroup"
              label="Organization Type ID"
              label-for="organizationTypeID"
              label-cols="9"
          >
              <b-form-input
                  name="organizationTypeID"
                  type="text"
                  v-model="gp.organisationTypeID"
              />
          </b-form-group>
          <b-form-group
              id="urlGroup"
              label="URL"
              label-for="url"
              label-cols="4"
          >
              <b-form-input name="url" type="text" v-model="gp.url" />
          </b-form-group>
          <b-form-group
              id="phoneGroup"
              label="Phone"
              label-for="phone"
              label-cols="5"
          >
              <b-form-input
                  name="phone"
                  type="number"
                  v-model="gp.phone"
              />
          </b-form-group>
          <b-form-group
              id="faxGroup"
              label="Fax"
              label-for="fax"
              label-cols="5"
          >
              <b-form-input
                  name="fax"
                  type="number"
                  v-model="gp.fax"
              />
          </b-form-group>
          <b-form-group
              id="emailGroup"
              label="Email"
              label-for="email"
              label-cols="4"
          >
              <b-form-input
                  name="email"
                  type="email"
                  v-model="gp.email"
              />
          </b-form-group>
          <b-form-group
              id="addressLine1Group"
              label="Address line 1"
              label-for="addressLine1"
              label-cols="3"
          >
              <b-form-input
                  name="addressLine1"
                  type="text"
                  v-model="gp.address.line1"
              />
          </b-form-group>
          <b-form-group
              id="addressLine2Group"
              label="Address line 2"
              label-for="addressLine2"
              label-cols="3"
          >
              <b-form-input
                  name="addressLine2"
                  type="text"
                  v-model="gp.address.line2"
              />
          </b-form-group>
          <b-form-group
              id="addressCityGroup"
              label="City"
              label-for="addressCity"
              label-cols="3"
          >
              <b-form-input
                  name="addressCity"
                  type="text"
                  v-model="gp.address.city"
              />
          </b-form-group>
          <b-form-group
              id="addressCountryGroup"
              label="Country"
              label-for="addressCountry"
              label-cols="3"
          >
              <b-form-input
                  name="addressCountry"
                  type="text"
                  v-model="gp.address.county"
              />
          </b-form-group>
          <b-form-group
              id="addressPostcodeGroup"
              label="Postcode"
              label-for="addressPostcode"
              label-cols="6"
          >
              <b-form-input
                  name="addressPostcode"
                  type="text"
                  v-model="gp.address.postcode"
              />
          </b-form-group>
        </div>
      </b-container>
    </b-modal>

    <b-modal 
      id="updateGPRecord" 
      title="Update GP Record" 
      v-model="showGPUpdateModal"
      :ok-title="gpUpdateFieldType === 'email' ? 'Set email to GP' : 'Set fax to GP'"
      @ok="updateGPContact"
      @cancel="clearGPContact" 
      @close="clearGPContact"
    >
      <template v-if="gpUpdateFieldType === 'email'">
        <b-form-group
          label="Email address:"
        >
          <b-form-input
            v-model="gpField"
            type="email"
            placeholder="Enter email"
            required
          ></b-form-input>
        </b-form-group>
      </template>
      <template v-if="gpUpdateFieldType === 'fax'">
        <b-form-group
          label="Fax:"
        >
          <b-form-input
            v-model="gpField"
            type="text"
            placeholder="Enter fax"
            required
          ></b-form-input>
        </b-form-group>
      </template>
    </b-modal>

    <b-modal 
      id="changeDeliveryAddressModal" 
      title="Change delivery address" 
      size="lg"
      centered
      @close="clearDeliveryAddressForm"
      :footer-bg-variant="footerBgVariant"
      :footer-text-variant="footerTextVariant"
    >
      <div class="delivery-modal-content">
        <!-- Current Address Display -->
        <div class="current-address-display">
          <div class="current-address-box">
            <h6 class="current-address-label">Current delivery address</h6>
            <p>{{ formattedDeliveryAddress }}</p>
          </div>
        </div>

        <hr />
        
        <AddressLookup
          :postcode-value.sync="newDeliveryAddress.postcode"
          :address-value.sync="newDeliveryAddress.address"
          postcode-label="Post code"
          address-label="Address"
          postcode-placeholder="Select postcode"
          address-placeholder="Select an address from the dropdown"
          :required="true"
          :submitted="addressSubmitted"
          :min-chars="1"
          @postcode-select="onPostcodeSelect"
          @addresses-loaded="onAddressesLoaded"
          @addresses-loading="onAddressesLoading"
          @error="onAddressError"
        />
      </div>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="primary"
            size="sm"
            class="w-100"
            @click="confirmDeliveryAddress"
          >
            Confirm delivery
          </b-button>
        </div>
      </template>
    </b-modal>

    <cancel-repeat-prescription-modal
      v-if="order.id"
      v-model="showRepeatCancelModal"
      :order-item-id="order.id"
      :patient-label="patientDisplayName"
      @success="onRepeatCancelSuccess"
    />
  </div>
</template>

<script>

import moment from 'moment'
import publicService from '../../services/PublicService'
import { gmapApi } from 'vue2-google-maps'
import OrderDetails from '../../components/OrderDetails'
import PatientDetails from '../../components/PatientDetails'
import PrescriptionDetails from '../../components/PrescriptionDetails'
import AdditionalOrderInfo from "../../components/AdditionalOrderInfo";
import VoucherInfo from "../../components/VoucherInfo";
import OrderHistory from "../../components/OrderHistory";
import OrderEventBus from '../../EventBus.js';
import CancelRepeatPrescriptionModal from '../../components/CancelRepeatPrescriptionModal';
import { canCancelRepeatCycle } from '../../pipes/RepeatCycleStatusPipe';
import AddressLookup from '../../components/AddressLookup.vue';
import { TIMELINE_EVENTS, paymentEventName, parseApiTime } from '../../utils/timelineEvents';
import pusherService from '@/services/PusherService';
import { sendDocumentPaymentLink } from '@/services/documentPaymentLink';

export default {
  name: 'Orders',
  components: {
    OrderDetails,
    PatientDetails,
    PrescriptionDetails,
    AdditionalOrderInfo,
    VoucherInfo,
    OrderHistory,
    AddressLookup,
    CancelRepeatPrescriptionModal
  },
  data: function () {
    return {
      order: {},
      prescription: {},
      patient: {},
      repeatFrequencyOptions: [
        { label: 'Weekly', days: 7 },
        { label: 'Fortnightly', days: 14 },
        { label: 'Monthly', days: 30 },
        { label: 'Every 3 months', days: 90 }
      ],
      gpList: null,
      totalGPPages: 0,
      currentPatientPage: 0,
      gpSearch: '',
      selectedGP: null,
      addresses: [],
      selectedAddress: null,
      isAddressLoading: false,
      gp: {
          odsCode: "",
          organizationID: "",
          organizationName: "",
          organisationTypeID: "",
          organisationType: "",
          url: "",
          phone: "",
          fax: "",
          email: "",
          address: {
              line1: "",
              line2: "",
              county: "",
              city: "",
              postcode: "",
          },
      },
      gpUpdateFieldType: null,
      gpField: '',
      showGPUpdateModal: false,
      gpNote: null,
      sickNote: null,
      refferalLetter: null,
      consLogTimes: null,
      repeatCycleProcessStatus: null,
      showRepeatCancelModal: false,
      showPrescription: false,
      travelPicker: false,
      deliveryOnly: false,
      doesVaccines: false,
      memberType: 'NONE',
      postcode: '',
      findPharmacyButtonClicked: false,
      bounds: {},
      showMap: false,
      pharmacies: [],
      pharmacyId: 0,
      confirmModal: false,
      newDeliveryAddress: {
        postcode: '',
        address: ''
      },
      addressSubmitted: false,
      postcodeOptions: [],
      antimalarialPrescriptionFields: [
        {
          key: 'name',
          label: 'Name'
        },
        {
          key: 'dosageStartDate',
          label: 'Start Date',
          tdClass: 'text-nowrap'
        },
        {
          key: 'dosageEndDate',
          label: 'End Date',
          tdClass: 'text-nowrap'
        },
        {
          key: 'dosageInstructions',
          label: 'Instructions'
        },
        {
          key: 'dosageExtraInfo',
          label: 'Extra Information',
        },
        {
          key: 'price',
          label: 'Price',
          tdClass: 'text-nowrap'
        }
      ],
      vaccinationPrescriptionFields: [
        {
          key: 'name',
          label: 'Name',
        },
        {
          key: 'startDate',
          label: 'Start Date',
          tdClass: 'text-nowrap'
        },
        {
          key: 'dosageInstructions',
          label: 'Instructions',
        },
        {
          key: 'price',
          label: 'Price',
          tdClass: 'text-nowrap'
        }
      ],
      completeRecord: {},
      history: {},
      deliveryDetails: null,
      consentLog: {},
      additionalPayments: {},
      completePaymentModal: false,
      completingAdditionalPayment: false,
      consentStatus: 'Pending',
      consentResendDocType: 'prescription',
      docTypes: [],
      timeline: [],
      formattedAddress: 'Loading...',
      sending: false,
      resending: false,
      initOtpSent: false,
      paymentEventUnsubscribe: null,
      documentAcceptanceUnsubscribe: null
    }
  },
  computed: {
    creationDate: function () {
      return moment(this.order.creationDate).local().format('Do MMMM YYYY, HH:mm');
    },
    repeatFrequencyLabel: function () {
      const freq = this.order && this.order.repeatFrequency;
      if (freq == null || freq === '') return 'N/A';
      // repeatFrequency is the minimum period (in days) between repeat releases
      const days = parseInt(freq, 10);
      if (isNaN(days)) return 'N/A';
      const match = this.repeatFrequencyOptions.find(o => o.days === days);
      return match ? match.label : `Every ${days} day${days === 1 ? '' : 's'}`;
    },
    repeatValidUntil: function () {
      const validUntil = this.order && this.order.nextRepeatPrescriptionDate;
      if (!validUntil) return 'N/A';
      return moment(validUntil).local().format('Do MMMM YYYY');
    },
    repeatCycleCancelable: function () {
      return canCancelRepeatCycle(this.repeatCycleProcessStatus);
    },
    patientDisplayName: function () {
      if (!this.patient) {
        return '';
      }
      return [this.patient.firstName, this.patient.lastName].filter(Boolean).join(' ');
    },
    repeatNoOfMonths: function () {
      const months = this.order && this.order.noRepeatPrscMonths;
      const value = parseInt(months, 10);
      if (isNaN(value) || value <= 0) return 'N/A';
      return `${value} month${value === 1 ? '' : 's'}`;
    },
    dateOfBirth: function () {
      return moment(this.order.patient.dateOfBirth).format('DD MMM YYYY');
    },
    price: function () {
      return '£' + this.order.price;
    },
    homePhone: function () {
      return this.order.patient.preferences && this.order.patient.preferences.homePhone ? this.order.patient.preferences.homePhone : 'Not set';
    },
    google: function() {
      return gmapApi;
    },
    isDocumentPaymentMade() {
      const paidStatuses = ['CAPTURED', 'COUPON', 'GBB-COUPON', 'GB COUPON', 'GB-COUPON', 'PRE-AUTHORIZED'];
      return paidStatuses.includes(this.consentLog.documentPaymentStatus);
    },
    hasAdditionalPayments() {
      const payments = this.additionalPayments || {};
      return !!(payments.documentPaymentStatus || payments.medicationPaymentStatus || payments.deliveryPaymentStatus);
    },
    isHomeDelivery() {
      return this.order && this.order.deliveryType === 'HOME';
    },
    isPharmacyDelivery() {
      return this.order && this.order.deliveryType === 'PHARMACY';
    },
    hasPrescriptionDocument() {
      return !!(this.prescription && this.prescription.id);
    },
    expectedNextStatus() {
      if (this.isHomeDelivery) return 'AWAITING_PHARMACY_DISPENSE';
      if (this.hasPrescriptionDocument) return 'WAITING_FOR_PATIENT_DELIVERY_DETAILS';
      return 'DELIVERED';
    },
    expectedNextStatusLabel() {
      const status = this.expectedNextStatus;
      if (status === 'AWAITING_PHARMACY_DISPENSE') return 'Awaiting Pharmacy Dispense';
      if (status === 'WAITING_FOR_PATIENT_DELIVERY_DETAILS') return 'Awaiting Patient Delivery Details';
      if (status === 'DELIVERED') return 'Complete';
      return status;
    },
    expectedEmailLabel() {
      if (this.isHomeDelivery) return 'the home-delivery emails (pharmacy and patient)';
      if (this.hasPrescriptionDocument) return 'the Digital RX email with authorization code';
      return 'document emails';
    },
    areRequiredPaymentsSettled() {
      if (this.isHomeDelivery) {
        return this.isPaymentSettled(this.additionalPayments.documentPaymentStatus)
          && this.isPaymentSettled(this.additionalPayments.medicationPaymentStatus)
          && this.isPaymentSettled(this.additionalPayments.deliveryPaymentStatus);
      }
      if (this.isPharmacyDelivery) {
        return this.isPaymentSettled(this.additionalPayments.documentPaymentStatus);
      }
      return false;
    },
    isAlreadyAtTargetStatus() {
      return this.order && this.order.status === this.expectedNextStatus;
    },
    canCompleteAdditionalPayment() {
      return this.hasAdditionalPayments
        && this.areRequiredPaymentsSettled
        && !this.isAlreadyAtTargetStatus;
    },
    isAdditionalPaymentMismatch() {
      return this.canCompleteAdditionalPayment;
    },
    isAnyDocumentApproved() {
      switch(this.consentResendDocType) {
        case 'prescription':
          return this.consentLog.isPrescriptionApproved === 'APPROVED';
        case 'referral':
          return this.consentLog.isReferralLetterApproved === 'APPROVED';
        case 'fitnote':
          return this.consentLog.isFitNoteApproved === 'APPROVED';
        default:
          return false;
      }
    },
    addressOptions() {
      return this.addresses.map(addr => ({
        value: addr,
        text: addr
      }))
    },
    formattedDeliveryAddress() {
      return this.formattedAddress || 'Loading...';
    },
    calculateTotalCharge() {
      return (documentType, deliveryMethod, additionalServiceFee, medicationFee, deliveryFee) => {
        const additionalServiceAmount = parseFloat(additionalServiceFee) || 0;
        const medicationAmount = parseFloat(medicationFee) || 0;
        const deliveryAmount = parseFloat(deliveryFee) || 0;

        if (documentType === 'prescription' && deliveryMethod === 'PHYSICAL') {
          return additionalServiceAmount + medicationAmount + deliveryAmount;
        }
        
        return additionalServiceAmount;
      };
    }
  },
  mounted: function () {
    this.getOrderDetails();
    this.getOrderHistory();
    OrderEventBus.$on('getOrderDetails', () => {
      this.getOrderDetails();
    })
  },
  beforeDestroy () {
    OrderEventBus.$off();
    
    if (this.paymentEventUnsubscribe) {
      this.paymentEventUnsubscribe();
    }
    
    if (this.documentAcceptanceUnsubscribe) {
      this.documentAcceptanceUnsubscribe();
    }
  },
  watch: {
    gpSearch(query){
      this.getGPList(query);
    },
    'order.id': function(newOrderId) {
      if (newOrderId) {
        this.fetchDeliveryAddress(newOrderId);
      }
    }
  },
  methods: {
    onPostcodeSelect() {
      this.selectedAddress = null
    },
    onAddressesLoading(isLoading) {
      this.isAddressLoading = isLoading
    },
    onAddressesLoaded(addresses) {
      this.addresses = addresses
    },
    onAddressError(error) {
      console.error('Address lookup error:', error)
    },
    async setupGPtoPatient(){
      const patientPayload = {
        ...this.patient,
        gp: this.selectedGP,
        // The backend reqquires the password field to be present with a value.
        password: 'qwerty',
        username: this.patient.email,
        preferences: {
          ...this.patient.preferences,
          gpPractice: this.selectedGP.organizationName,
          gpAddress: JSON.stringify(this.selectedGP.address)
        },
      }

      delete patientPayload.displayName;
      await this.$patientService.updatePatient(patientPayload);
      this.getOrderDetails();
    },
    async addGP() {
      const gpAddResult = await this.$gpService.addGP(this.gp);
      this.selectedGP = gpAddResult;
      this.setupGPtoPatient();
      this.clearGP();
    },
    clearGP() {
        this.gp = {
            odsCode: "",
            organizationID: "",
            organizationName: "",
            organisationTypeID: "",
            organisationType: "",
            url: "",
            phone: "",
            fax: "",
            email: "",
            address: {
                line1: "",
                line2: "",
                county: "",
                city: "",
                postcode: "",
            },
        };
    },
    async updateGPContact(){
      const gpPayload = {
        ...this.patient.gp,
      }

      if (this.gpUpdateFieldType === 'email') {
        gpPayload.email = this.gpField;
      } else {
        gpPayload.fax = this.gpField;
      }

      try {
        await this.$gpService.updateGP(this.patient.gp?.id, gpPayload);
      } catch (error) {
        this.$awn.alert(error.response.data.message, 'Error');
      }

      this.clearGPContact();  
      this.getOrderDetails();
    },
    clearGPContact(){
      this.gpUpdateFieldType = null
      this.gpField = ''
      this.showGPUpdateModal = false
    },
    async onClickShareNotesWithGP(){
      try {
        this.$awn.tip(`Please wait. Sending ${this.patient.gp.email ? 'email' : 'fax'} to GP`);
        await this.$gpService.shareNotes(this.order?.id, this.patient.gp.email ? 'email' : 'fax');
        this.$awn.info('Operation finished.');
      } catch (error) {
        this.$awn.alert(error.response.data.message, 'Error');
      }
      await this.getOrderDetails();
    },
    async getOrderDetails () {
      const orderResult = await this.$orderService.getOrder(this.$route.params.orderId);
      this.order = orderResult;
      this.deliveryOnly = this.order.deliveryType == 'HOME' ? true : false;

      this.repeatCycleProcessStatus = null;
      if (orderResult.isRepeatPrescription) {
        try {
          const repeatDetail = await this.$repeatPrescriptionService.getRepeatPrescriptionDetail(orderResult.id);
          this.repeatCycleProcessStatus = repeatDetail && repeatDetail.processStatus;
        } catch (error) {
          this.repeatCycleProcessStatus = null;
        }
      }

      try {
        const consLogTimesRes = await this.$orderService.getConsultationLogTimes(this.$route.params.orderId);
        this.consLogTimes = consLogTimesRes.data;
      } catch (error) {
        this.consLogTimes = null;
      }

      try {
        const deliveryDetailsRes = await this.$orderService.getOrderDeliveryDetails(this.$route.params.orderId);
        this.deliveryDetails = deliveryDetailsRes.data;
      } catch (error) {
        this.deliveryDetails = null;
      }
      
			if (orderResult.type == 'CHAT_ASSESSMENT') {
				try {
					const gpNoteResult = await this.$orderService.getChatGPNotes(orderResult.id);
					this.gpNote = gpNoteResult;
				} catch (error) {
					this.handleError(error);
				}

				try {
					const sickNoteResult = await this.$orderService.getChatSickNotes(orderResult.id);
					this.sickNote = sickNoteResult;
				} catch (error) {
					this.handleError(error);
				}

				try {
					const refferalLetterResult = await this.$orderService.getChatReferrelLetter(orderResult.id);
					this.refferalLetter = JSON.parse(refferalLetterResult.text);
				} catch (error) {
					this.handleError(error);
				}
			} else {
				if (orderResult.appointment) {
					try {
						const gpNoteResult = await this.$orderService.getGPNotes(orderResult.appointment.id);
						this.gpNote = gpNoteResult;
					} catch (error) {
						this.handleError(error);
					}

					try {
						const sickNoteResult = await this.$orderService.getSickNotes(orderResult.appointment.id);
						this.sickNote = sickNoteResult;
					} catch (error) {
						this.handleError(error);
					}

					try {
						const refferalLetterResult = await this.$orderService.getReferrelLetter(orderResult.appointment.id);
						this.refferalLetter = JSON.parse(refferalLetterResult.text);
					} catch (error) {
						this.handleError(error);
					}
				}
			}
			
      const patientResult = await this.$patientService.getPatient(orderResult.patient?.id);
      this.patient = patientResult;

      if (!this.patient.gp) {
        await this.getGPList();
      }

      if (this.order.pharmacy) {
        this.postcode = this.order.pharmacy.address ? this.order.pharmacy.address.postcode : '';
        this.pharmacyId = this.order.pharmacy?.id;
      }

      if (this.order.transcriptionTransferRecordDTOS) {
        this.completeRecord = this.order.transcriptionTransferRecordDTOS.find(
          rec => rec.status === 'COMPLETED'
        );
      }

      if(this.$route.params.orderId) {
        try {
          const prescriptionResult = await this.$orderService.getPrescription(this.$route.params.orderId);
          if (prescriptionResult.medication) {
            prescriptionResult.medication.forEach(m => {
              for (var property in m.formulary) {
                if (Object.prototype.hasOwnProperty.call(m.formulary, property)) {
                  m[property] = m.formulary[property];
                }
              }
            })
          }
          this.prescription = prescriptionResult;
        } catch (error) {
          console.warn('Prescription not available for this order:', error);
        }
      }

      await this.getConsentLog();
      await this.fetchAdditionalPayments();
      
      if (this.documentAcceptanceUnsubscribe) {
        this.documentAcceptanceUnsubscribe();
      }
      this.documentAcceptanceUnsubscribe = pusherService.listen('consultation_log', 'document_acceptance ', (data) => {
        if (data && (data.isPrescriptionCreated === 'CREATED' || data.isReferralLetterCreated === 'CREATED' || data.isFitNoteCreated === 'CREATED') ) {
          if (data.orderItemId === this.order.id) {
            if (this.consentLog) {
              this.$set(this.consentLog, 'isPrescriptionCreated', data.isPrescriptionCreated);
              this.$set(this.consentLog, 'isReferralLetterCreated', data.isReferralLetterCreated);
              this.$set(this.consentLog, 'isFitNoteCreated', data.isFitNoteCreated);
            }
          }
        }
      });

      if (this.paymentEventUnsubscribe) {
        this.paymentEventUnsubscribe();
      }
      this.paymentEventUnsubscribe = pusherService.listen('payment', 'payment-event', (data) => {
        const paymentData = typeof data === 'string' ? JSON.parse(data) : data;
        
        if (paymentData && paymentData.status === 'CAPTURED' && (paymentData.paymentFor === 'DOCUMENTS' || paymentData.paymentFor === 'DOCUMENTS|MEDICATION|DELIVERY')) {
          if (paymentData.orderItemId === this.order.id) {
            if (this.consentLog) {
              this.$set(this.consentLog, 'documentPaymentStatus', paymentData.status);
            }
            this.$set(this.additionalPayments, 'documentPaymentStatus', paymentData.status);
            this.fetchAdditionalPayments();
          }
        }
      });
      
      this.findPharmacies();
    },
    async getOrderHistory() {
      const orderHistory = await this.$orderService.getOrderHistory(this.$route.params.orderId);
      let historyLog = [];
      
      if (orderHistory.data.transcriptionRequest?.dateTime) {
        historyLog.push(
          {
            at: orderHistory.data.transcriptionRequest.dateTime,
            status: "reserved",
            title: "Transcription requested by",
            by: orderHistory.data.transcriptionRequest.doctorName,
            note: "Initial consultation was completed",
            type: "past"
          }
        );
      }
      
      if (orderHistory?.data?.reserverForTranscription.length>0 && orderHistory?.data?.reserverForTranscription[0]?.dateTime) {
        orderHistory.data.reserverForTranscription.forEach(el => {
          historyLog.push(
            {
              at: el.dateTime,
              status: "reserved",
              title: "Reserved for transcription by",
              by: el.doctorName,
              note: "Reservation",
              type: "past"
            }
          );
        });
      }
      
      if (orderHistory.data.releaseForTranscription[0]?.dateTime) {
        orderHistory.data.releaseForTranscription.forEach(el => {
          historyLog.push(
            {
              at: el.dateTime,
              status: "reserved",
              title: "Released from transcription by",
              by: el.doctorName,
              note: "Release",
              type: "past"
            }
          );
        });
      }
      
      if (orderHistory.data.rejectedForTranscription[0]?.dateTime) {
        orderHistory.data.rejectedForTranscription.forEach(el => {
          historyLog.push(
            {
              at: el.dateTime,
              status: "rejected",
              title: "Rejected from transcription by",
              by: el.doctorName,
              note: "Reject",
              type: "past"
            }
          );
        });
      }

      if (orderHistory.data.completeConsultation?.dateTime) {
        historyLog.push(
          {
            at: orderHistory.data.completeConsultation.dateTime,
            status: "completed",
            title: "Completed by",
            by: orderHistory.data.completeConsultation.doctorName,
            note: "Consultation completed",
            type: "past"
          }
        );
      }

      historyLog.sort((a, b) => new Date(a.at) - new Date(b.at));
      historyLog[historyLog.length - 1].type = 'current';

      let cleanedRecords = historyLog.filter((rec, i, arr) => {
        const prev = arr[i - 1];
        return !(
          rec.title.includes("Released from transcription") &&
          prev &&
          prev.status === "rejected"
        );
      });
      
      this.history = cleanedRecords;
    },
    paymentStatusLabel(status) {
      return status || 'N/A';
    },
    isPaymentSettled(status) {
      if (!status) return false;
      const settled = ['CAPTURED', 'PRE-AUTHORIZED', 'PRE-AUTHORISED', 'COUPON', 'GB-COUPON', 'GB COUPON', 'GBB-COUPON', 'N/A'];
      return settled.includes(String(status).toUpperCase()) || settled.includes(status);
    },
    async fetchAdditionalPayments() {
      try {
        const paymentResponse = await this.$orderService.getAdditionalPaymentStatus(this.$route.params.orderId);
        this.additionalPayments = (paymentResponse && paymentResponse.data) ? paymentResponse.data : {};
        if (this.consentLog) {
          this.consentLog = {
            ...this.consentLog,
            ...this.additionalPayments
          };
        }
      } catch (error) {
        this.additionalPayments = {};
        console.warn('Failed to fetch payment status:', error);
      }
    },
    async completeAdditionalPayment(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      this.completingAdditionalPayment = true;
      try {
        const response = await this.$orderService.completeAdditionalPayment(this.$route.params.orderId);
        const payload = response && response.data ? response.data : response;
        const nextStatus = payload && payload.newStatus ? payload.newStatus : this.expectedNextStatusLabel;
        this.$awn.success(`Order moved to ${nextStatus}. Email sent.`);
        this.completePaymentModal = false;
        await this.getOrderDetails();
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message)
          || 'Failed to complete additional payment';
        this.$awn.alert(message, 'Error');
      } finally {
        this.completingAdditionalPayment = false;
      }
    },
    async getConsentLog() {
      const orderHistory = await this.$orderService.getConsentLog(this.$route.params.orderId);

      if (orderHistory.status == 'OK' && orderHistory.data && orderHistory.data.length > 0) {
        this.consentLog = orderHistory.data[orderHistory.data.length - 1];
        
        try {
          const paymentResponse = await this.$orderService.getAdditionalPaymentStatus(this.$route.params.orderId);
          if (paymentResponse && paymentResponse.data) {
            this.consentLog = {
              ...this.consentLog,
              ...paymentResponse.data
            };
          }
        } catch (error) {
          console.warn('Failed to fetch payment status:', error);
        }

        try {
          const deliveryAddressResponse = await this.$orderService.getDeliveryAddress(this.$route.params.orderId);
          if (deliveryAddressResponse && deliveryAddressResponse.data) {
            this.consentLog = {
              ...this.consentLog,
              deliveryAddressUpdateTime: deliveryAddressResponse.data.creationDate
            };
          }
        } catch (error) {
          console.warn('Failed to fetch delivery address:', error);
        }
        
        this.buildTimeline(this.consentLog);
        this.buildDocTypes(this.consentLog);
      }

      if (this.order.mediaType == 'AUDIO') {
        this.docTypes = [
          {
            key: 'prescription',
            value: 'Prescription',
          },
          {
            key: 'fitnote',
            value: 'Fit Note',
          },
          {
            key: 'referral',
            value: 'Referral Letter',
          }
        ];
      }
    },
    buildTimeline(data) {
      this.timeline = TIMELINE_EVENTS
        .filter(e => {
          if (!data[e.key]) return false
          if (e.conditionKey && !data[e.conditionKey]) return false
          return true
        })
        .map(e => {
          let name = e.name
          let tone = null

          if (e.statusKey) {
            const payment = paymentEventName(e.name, data[e.statusKey]);
            name = payment.name;
            tone = payment.tone;
          }

          if (e.key === 'prescriptionProposedTime') {
            const prescriptionType = this.prescription?.prescriptionType;
            if (prescriptionType == 'PHYSICAL') {
              name = 'Home Delivery Prescription Proposed';
            } else {
              name = 'Digital Prescription Proposed';
            }
          }

          if (e.proposerKey && data[e.proposerKey]) {
            name = `${name} by the ${data[e.proposerKey].toLowerCase()}`
          }

          const time = parseApiTime(data[e.key])
          return {
            key: e.key,
            rawTime: time || new Date(0),
            timestamp: time ? this.formatTime(time) : 'Time not recorded',
            name,
            tone
          }
        })
        .sort((a, b) => b.rawTime - a.rawTime)
    },
    buildDocTypes(data) {
      const docMap = {
        prescription: {
          proposed: 'prescriptionProposedTime',
          created: 'prescriptionCreatedTime'
        },
        fitnote: {
          proposed: 'fitNoteProposedTime',
          created: 'fitNoteCreatedTime'
        },
        referral: {
          proposed: 'referralLetterProposedTime',
          created: 'referralLetterCreatedTime'
        }
      }

      const labels = {
        prescription: 'Prescription',
        fitnote: 'Fit note',
        referral: 'Referral letter'
      }

      this.docTypes = Object.keys(docMap)
        .filter(key => {
          const { proposed, created } = docMap[key]
          return data[proposed] || data[created]
        })
        .map(key => ({
          key: key,
          value: labels[key]
        }))

      if (this.docTypes.length > 0 && !this.docTypes.find(d => d.key === this.consentResendDocType)) {
        this.consentResendDocType = this.docTypes[0].key;
      }
    },
    formatTime(time) {
      const d = new Date(time)
      const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      const clock = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      return `${date} | ${clock}`
    },
    async resendConsent() {
      let key = '';

      switch (this.consentResendDocType.toLowerCase()) {
        case 'prescription':
          key = 'isPrescriptionProposed';
          break;
        case 'fitnote':
          key = 'isFitNoteProposed';
          break;
        case 'referral':
          key = 'isReferralLetterProposed';
          break;
      }

      if (this.isDocumentPaymentMade) {
        this.$awn.alert("Document is already paid for.");
        return;
      }
      
      const payload = {
        message: "Document Acceptance request",
        emailTemplateName: "confirm-additional-services.html",
        whatsAppTemplate: "HX10c69cf6946cb299b7165b42f88f1ee7",
        subject: "Payment Confirmation",
        isSmsEnable: false,
        isWhatsAppEnable: true,
        isEmailEnable: true,
        type: "DOCUMENT-ACCEPTANCE-REQUEST",
        heading: "Document Acceptance request",
        url1: `${process.env.VUE_APP_PATIENT_URL}document-request/${this.order.id}/${this.consentResendDocType.toLowerCase()}/${this.order.patient.email}/${this.order.appointment.datetime}`,
        orderItemId: this.order.id,
        documentType: this.consentResendDocType.toLowerCase()
      };

      try {
        const res = await this.$orderService.resendDocumentConsent(payload);

        if (res.status === "OK") {
          await this.$orderService.saveLogItem(
            this.order.id,
            key,
            "PROPOSED"
          );
          this.$awn.success("Message sent");
          this.getConsentLog();
        }
      } catch (error) {
        this.$awn.alert("Failed to resend consent request");
      }
    },
    async resendPaymentLink() {
      if (this.isDocumentPaymentMade) {
        this.$awn.alert("Document is already paid for.");
        return;
      }
      this.sending = true;
      try {
        await sendDocumentPaymentLink(this.$orderService, {
          orderId: this.order.id,
          patientId: this.order.patient.id,
          email: this.order.patient.email,
          docType: this.consentResendDocType,
          prescriptionType: this.prescription?.prescriptionType
        });
        this.initOtpSent = true;
        this.$awn.success("Message sent");
      } catch (error) {
        this.$awn.alert(error && error.message ? error.message : "Failed to resend payment request");
      } finally {
        this.sending = false;
        this.resending = false;
      }
    },
    async getGPList(query){
      const gpResult = await this.$gpService.search(query, this.currentPatientPage);
      this.gpList = gpResult.content;
      this.totalGPPages = gpResult.totalPages;
    },
    getDateFotmatted(raw) {
      const cleaned = raw.replace(/\[.*\]/, '');
      return new Date(cleaned).toISOString();
    },
    handleError(error){
      if (error.response) {
        // The request was made and the server responded with a status code that falls out of the range of 2xx
      } else if (error.request) {
        // The request was made but no response was received `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
      } else {
        // Something happened in setting up the request that triggered an Error
        this.displayNotification('error', error.message)
      }
    },
    openUpdateGPModal(field){
      this.gpUpdateFieldType = field;
      this.showGPUpdateModal = true;
    },
    getGPContactNumber(phone){
      if (!phone) {
        return 'N/A'
      }
      return '+44' + phone.slice(1)
    },
    getGPAddress(address){
      const line1 = address.line1 ? `${address.line1},` : '';
      const line2 = address.line2 ? `${address.line2},` : '';
      const county = address.county ? `${address.county},` : '';
      const city = address.city ? `${address.city}` : '';
      return `${line1} ${line2} ${county} ${city}`;
    },
    isSelectedGP(item){
      return item?.id === this.selectedGP?.id;
    },
    displayNotification(type, message) {
      if(type === 'error') {
          this.$awn.alert(message, 'Error');
          this.$router.push('/login')
      }
    },
    dateTime(date) {
      return moment(date).local().format('Do MMMM YYYY, HH:mm');
    },
    findPharmacies() {
      this.showMap = false;
      this.findPharmacyButtonClicked = true;
      this.getLatLan(`${this.postcode},UK`).then(location => {
        this.postCodeLocation = location;
        if (location) {
          publicService.getNearestPharmacies(location.lat(), location.lng(), 5, this.deliveryOnly, this.memberType).then(pharmacies => {
            this.pharmacies = pharmacies;
            if (this.pharmacies.length > 2) {
              const maxLat = this.pharmacies.map(p => p.latitude).reduce((l1, l2) => Math.max(l1, l2));
              const maxLng = this.pharmacies.map(p => p.longitude).reduce((l1, l2) => Math.max(l1, l2));
              const minLat = this.pharmacies.map(p => p.latitude).reduce((l1, l2) => Math.min(l1, l2));
              const minLng = this.pharmacies.map(p => p.longitude).reduce((l1, l2) => Math.min(l1, l2));

              this.bounds = {
                  south: minLat,
                  west: minLng,
                  north: maxLat,
                  east: maxLng
              };
            } else {
              // if (this.pharmacies.length === 0) {
              //     this.showNoPharmaciesModal = true;
              // }

              const r = 0.5;
              this.bounds = {
                south: location.lat() - r,
                west: location.lng() - r,
                north: location.lat() + r,
                east: location.lng() + r
              };
            }
            this.showMap = true;
            this.$nextTick(() => {
              if(this.$refs.map) {
                this.$refs.map.$mapPromise.then((map) => {
                  map.fitBounds(this.bounds);
                });
              }
            });
          });
        } else {
          this.pharmacies = [];
        }
      });
    },
    async getLatLan(address) {
      await this.$gmapApiPromiseLazy();
      const google = this.google();
      return await new Promise((resolve, reject) => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ 'address': address }, function (results, status) {
          if (status === google.maps.GeocoderStatus.OK) {
              resolve(results[0].geometry.location);
          } else {
              reject(status);
          }
        });
      })
    },
    selectPharmacy(p) {
      this.pharmacyId = p?.id;
    },
    reset() {
      this.getOrderDetails()
    },
    confirm() {
      this.confirmModal = false;
      let deliveryType = this.deliveryOnly ? 'HOME' : 'PHARMACY';
      this.$orderService.setDeliveryDetails(this.order?.id, this.pharmacyId, deliveryType)
        .then(() => {
          this.$router.push('/orders/waiting-for-admin')
          this.$awn.success('Delivery details confirmed successfully')
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            this.$awn.alert(error.response.data.message);
          } else {
            this.$awn.alert('There was a network error');
          }
        });
    },
    addDeliveryNotes() {
      this.$orderService.addDeliveryNotes(this.order?.id, this.order.workflow.PHARMACY_DELIVER_NOTES).then(() => {
        this.$awn.success("Delivery notes added successfully to order " + this.order?.id);
        this.$router.push('/orders/all');
      }).catch(error => {
        this.$awn.alert(error, "Oops");
      });
    },
    handleAppointmentChange() {
      this.getOrderDetails();
    },
    clearDeliveryAddressForm() {
      this.newDeliveryAddress = {
        postcode: '',
        address: ''
      };
      this.postcodeOptions = [];
      this.addressOptions = [];
    },
    formatDateTime(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);

      if (isNaN(date.getTime())) return '';

      const options = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      };

      const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);

      const get = type => parts.find(p => p.type === type)?.value;

      const hour = get('hour');
      const minute = get('minute');
      const dayPeriod = get('dayPeriod')?.toLowerCase();
      const day = get('day');
      const month = get('month');
      const year = get('year');

      return `${hour}:${minute}${dayPeriod}, ${day} ${month} ${year}`;
    },
    fetchDeliveryAddress(orderId) {
      this.$orderService.getDeliveryAddress(orderId)
        .then(response => {
          if (response && response.data) {
            const addr = response.data;
            const parts = [
              addr.line1,
              addr.line2,
              addr.city,
              addr.postcode,
              addr.country
            ].filter(Boolean);
            this.formattedAddress = parts.join(', ') || this.getPatientAddress();
          } else {
            this.formattedAddress = this.getPatientAddress();
          }
        })
        .catch(error => {
          console.error('Error fetching delivery address:', error);
          this.formattedAddress = this.getPatientAddress();
        });
    },
    getPatientAddress() {
      if (!this.order.patient) return 'No address available';
      
      const patient = this.order.patient;
      const address = patient.address || {};
      
      const parts = [
        address.line1,
        address.line2,
        address.city,
        address.county,
        patient.postcode
      ].filter(Boolean);
      
      return parts.join(', ') || 'No address available';
    },
    onRepeatCancelSuccess () {
      this.$awn.success('Repeat prescription cancelled. The patient has been notified.');
      this.getOrderDetails();
    },
    getOrderStatus() {
      if (!this.deliveryDetails) {
        return '';
      }
      if (this.deliveryDetails.status === 'DELIVERED') {
        return 'Order delivered';
      } else if (this.deliveryDetails.status === 'ORDER_DISPENSED') {
        return 'Order dispensed';
      }
      
      return '';
    },
    openDeliveryAddressModal() {
      this.$bvModal.show('changeDeliveryAddressModal');
    },
    copyTrackingId() {
      if (this.deliveryDetails && this.deliveryDetails.trackingId) {
        navigator.clipboard.writeText(this.deliveryDetails.trackingId).then(() => {
          this.$awn.success('Tracking ID copied to clipboard');
        }).catch(() => {
          this.$awn.alert('Failed to copy tracking ID', 'Error');
        });
      }
    },
    confirmDeliveryAddress() {
      if (!this.newDeliveryAddress.postcode || !this.newDeliveryAddress.address) {
        this.$awn.alert('Please select both postcode and address');
        return;
      }

      const addressParts = this.newDeliveryAddress.address.split(',').map(part => part.trim());
      
      this.$orderService.addDeliveryAddress(
        this.order.patient.id,
        this.order.id,
        this.newDeliveryAddress.postcode,
        this.newDeliveryAddress.address,
        addressParts[0] || '',
        addressParts[1] || '',
        addressParts[2] || '',
        addressParts[3] || ''
      ).then(() => {
        this.$awn.success('Delivery address updated successfully');
        this.$bvModal.hide('changeDeliveryAddressModal');
        this.clearDeliveryAddressForm();
        this.getOrderDetails();
        this.fetchDeliveryAddress(this.order.id);
      }).catch(error => {
        if (error.response && error.response.data) {
          this.$awn.alert(error.response.data.message || 'Failed to update delivery address');
        } else {
          this.$awn.alert('Failed to update delivery address');
        }
      });
    }
  }
}
</script>

<style lang="scss">
  #pharmacyIdDescription small {
    margin-top: -1.8%;
  }
  .collapsed > .when-opened, :not(.collapsed) > .when-closed {
    display: none;
  }

  .gp-list-container {
    row-gap: 1rem;

    .gp-item {
      padding: 0.5rem;
      border: 1px solid #d8d8d8;
      border-radius: 5px;
      text-align: left;
      cursor: pointer;

      &.selected {
        border: 2px solid #6eb65e !important;
      }

      p {
        margin: 0;
      }
    }
  }

  .call-details-box {
    border: 1px solid #0000001A;
    padding: 0 6px;

    p {
      margin: 4px 0;

      .key {
        color: #006D32;
        font-weight: 600;
      }

      .val {
        font-weight: 500;
        float: right;
      }
    }
  }

  .fw-600 {
    font-weight: 600;
  }

  .additional-services {
    .payment-status-panel {
      padding: 8px 0 12px;

      .title {
        margin-bottom: 8px;
        font-weight: 600;
        font-size: 16px;
        color: #151B1E;
      }

      p {
        margin-bottom: 4px;
        font-size: 14px;
      }

      .key {
        color: #646464;
        font-weight: 600;
      }

      .val {
        margin-left: 4px;
        font-weight: 500;
      }
    }

    .new-feature-badge {
      color: #155724;
      font-weight: 700;
      letter-spacing: 0.4px;
    }

    .action-row {
      padding: 8px;
      border: none;
      border-radius: 8px;
      background-color: #FFF7DA;

      p {
        margin-bottom: 4px;
        font-size: 14px;
      }

      .title {
        font-size: 16px;
        color: #333333;
      }

      .key {
        color: #896E0E99;
      }

      .val {
        font-weight: 500;
        margin-left: 4px;
      }

      .pending {
        color: #006DC5;
      }

      .approved {
        color: #006D32;
      }

      .declined {
        color: #8B0000;
      }
    }

    .share-docs-row {
      .title {
        margin-bottom: 4px;
        font-weight: 600;
        font-size: 16px;
        color: #151B1E;
      }

      .subtitle {
        margin-bottom: 4px;
        font-weight: 400;
        font-size: 14px;
        color: #333333;
      }

      .doc-type-select {
        min-width: 180px;
        height: 35px;
        border: 1px solid #0000001A;
        border-radius: 4px;
      }
    }

    .resend-btn {
      min-width: 120px;
      height: 35px;
      margin-top: 8px;
      border: 1px solid #C2CFD6;
      background-color: #C2CFD6;
    }

    .history-row {
      .title {
        font-weight: 600;
        font-size: 16px;
        color: #151B1E;
      }

      .history-item {
        position: relative;
        border: 1px solid #0000001A;
        border-radius: 8px;
        margin-bottom: 16px;

        p {
          margin: 8px;
        }

        .timestamp {
          display: inline-block;
          font-size: 14px;
          font-weight: 400;
          color: #0E2119;
          margin: 2px 16px 2px 0;
          padding: 2px 4px;
          border-radius: 4px;
          background-color: #DAEDE5;
        }

        .name {
          font-size: 14px;
          font-weight: 400;
          color: #141313;
        }

        &:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 100%;
          height: 16px;
          border-left: 2px dotted #4FAA84;
        }
      }

      .pending {
        background-color: #FFF7DA;

        .name {
          color: #CA7C08;
        }
      }

      .failed {
        background-color: #FDECEA;
        border-color: #F5C2BD;

        .name {
          color: #B42318;
          font-weight: 600;
        }
      }

      .complete {
        background-color: #DAEDE5;

        .name {
          color: #006D32;
        }
      }
    }
  }

  .delivery-address-block {
    margin-bottom: 1rem;

    .delivery-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;
      color: #151B1E;
    }

    .delivery-address-box {
      padding: 8px 12px;
      background: #F0F3F5;
      border: 1px solid #C2CFD6;

      .delivery-address-text {
        font-weight: 400;
        font-size: 12px;
        line-height: 100%;
        color: #3E515B;
        margin-bottom: 0px;
      }
    }
  }

  .delivery-info-block {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: #364A8A1A;
    border: 1px solid #364A8A26;

    .delivery-info-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .delivery-info-header {
        display: flex;
        align-items: center;
        gap: 1rem;

        .delivery-info-label {
          font-weight: 400;
          font-size: 14px;
          line-height: 19px;
          color: #364A8A;
          margin: 0;
        }

        .order-status-badge {
          padding: 4px 8px;
          border-radius: 16px;
          background: #FFF7DA;
          font-weight: 400;
          font-size: 14px;
          line-height: 19px;
          color: #896E0E;
        }
      }

      .delivery-carrier {
        margin: 0;
        color: #333;
        font-size: 0.95rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
          color: #364A8A;
          font-size: 0.5rem;
        }

        .carrier-name {
          font-weight: 400;
          font-size: 14px;
          line-height: 100%;
          color: #364A8A;
        }

        .tracking-id {
          @extend .carrier-name;
          
          color: #646464;
          margin-left: 0.5rem;
        }
      }

      .copy-id-btn {
        border-radius: 4px;
        padding: 8px;
        white-space: nowrap;
        background: #FFFFFF;
        border: 1px solid #3C896966;
        color: #3C8969;

        &:hover {
          background-color: #f0f4f8;
          border-color: #4a7ba7;
        }

        i {
          margin-right: 0.5rem;
        }
      }
    }
  }

  #changeDeliveryAddressModal {
    .modal-footer {
      border-top: none;
    }
  }

  .delivery-modal-content {
    padding: 0.5rem 0;

    .current-address-display {
      .current-address-box {
        display: flex;
        flex-direction: column;
        gap: 8px;
        border-radius: 8px;
        padding: 16px;
        background: #FFFFFF;
        border: 1px solid #D9D9D9;

        .current-address-label {
          font-weight: 400;
          font-size: 16px;
          line-height: 21px;
          color: #111111;
          margin: 0;
        }

        p {
          margin: 0;
          font-weight: 400;
          font-size: 14px;
          line-height: 19px;
          color: #333333;
        }
      }
    }

    .text-danger {
      color: #dc3545;
    }
  }
</style>
